/**
 * `/errors` — the error-triage board (migration 186).
 *
 * Lists error INCIDENTS (one row per distinct signature) and, per incident, the
 * AI qualification + the ready-to-paste IDE fix-prompt an operator copies into
 * an IDE agent. Reads + mutates entirely through the chokepoint
 * (`dispatchAction` → `POST /api/dispatch/:action`, as `human:staff`), so the
 * page owns NO bespoke endpoint (`api: []` in page-registry, like `/reports`):
 * `list_error_incidents` for the board + detail, `update_error_incident` for the
 * human disposition (resolve / dismiss / reopen / note).
 *
 * The board is DEFECT-first — the detector only stores real defects; the
 * business-rejection / transient noise never lands here (blocker 2). The prompt
 * is written PII-scrubbed by the recon lane (blocker 4), so it renders in full.
 */

import { useCallback, useEffect, useState } from "react";
import { Link, useParams } from "react-router-dom";
import { dispatchAction } from "../lib/dispatch";
import { studioTimeZone } from "../lib/studio-tz";

// ---------------------------------------------------------------------------
// Wire shapes (mirror the `list_error_incidents` output).
// ---------------------------------------------------------------------------

type ErrorIncidentStatus =
  | "new"
  | "triaging"
  | "qualified_expected"
  | "prompt_ready"
  | "dismissed"
  | "resolved"
  | "regressed";

type ErrorIncidentSource =
  | "audit_log_defect"
  | "dlq"
  | "stranded_pending"
  | "ci"
  | "railway";

type ErrorClass = "defect" | "business_rejection" | "transient";

interface ErrorIncidentRow {
  readonly id: string;
  readonly signature: string;
  readonly source: ErrorIncidentSource;
  readonly action: string;
  readonly outcome_code: string;
  readonly error_class: ErrorClass;
  readonly discriminators: Record<string, unknown>;
  readonly title: string;
  readonly first_seen_at: string;
  readonly last_seen_at: string;
  readonly occurrence_count: number;
  readonly sample_refs: ReadonlyArray<Record<string, unknown>>;
  readonly status: ErrorIncidentStatus;
  readonly qualification: Record<string, unknown> | null;
  readonly suggested_prompt: string | null;
  readonly prompt_template_version: string | null;
  readonly resolved_at: string | null;
  readonly resolved_by: string | null;
  readonly notes: string | null;
  readonly created_at: string;
  readonly updated_at: string;
}

interface ListErrorIncidentsOutput {
  readonly incidents: ReadonlyArray<ErrorIncidentRow>;
  readonly count_returned: number;
  readonly limit: number;
}

const STATUSES: ReadonlyArray<ErrorIncidentStatus> = [
  "new",
  "triaging",
  "qualified_expected",
  "prompt_ready",
  "dismissed",
  "resolved",
  "regressed",
];
const SOURCES: ReadonlyArray<ErrorIncidentSource> = [
  "audit_log_defect",
  "dlq",
  "stranded_pending",
  "ci",
  "railway",
];

// ---------------------------------------------------------------------------
// Small presentational helpers.
// ---------------------------------------------------------------------------

function fmtDateTime(iso: string | null): string {
  if (iso === null) return "—";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return iso;
  return d.toLocaleString("en-GB", {
    timeZone: studioTimeZone(),
    day: "2-digit",
    month: "short",
    hour: "2-digit",
    minute: "2-digit",
  });
}

/** A status pill. `regressed` is the highest-signal state, so it reads loud. */
function StatusBadge({ status }: { readonly status: ErrorIncidentStatus }): JSX.Element {
  return <span className={`badge badge-status-${status}`}>{status.replace(/_/g, " ")}</span>;
}

function ClassBadge({ cls }: { readonly cls: ErrorClass }): JSX.Element {
  return <span className={`badge badge-class-${cls}`}>{cls.replace(/_/g, " ")}</span>;
}

// ---------------------------------------------------------------------------
// List view — `/errors`.
// ---------------------------------------------------------------------------

export function Errors(): JSX.Element {
  const [incidents, setIncidents] = useState<ReadonlyArray<ErrorIncidentRow> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [statusFilter, setStatusFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [classFilter, setClassFilter] = useState<string>("");

  const load = useCallback(async (): Promise<void> => {
    setLoading(true);
    setError(null);
    const input: Record<string, unknown> = { limit: 200 };
    if (statusFilter) input["status"] = statusFilter;
    if (sourceFilter) input["source"] = sourceFilter;
    if (classFilter) input["error_class"] = classFilter;
    const r = await dispatchAction<Record<string, unknown>, ListErrorIncidentsOutput>(
      "list_error_incidents",
      input,
    );
    if (r.ok) {
      setIncidents(r.response.incidents);
    } else {
      setError(`${r.message} (${r.code})`);
      setIncidents(null);
    }
    setLoading(false);
  }, [statusFilter, sourceFilter, classFilter]);

  useEffect(() => {
    void load();
  }, [load]);

  // Freshness proxy: the newest activity across incidents. A truly-empty board
  // proves liveness on /status (the detector's cron_freshness capability), which
  // this chip points to rather than overstating "live" from zero rows.
  const lastActivity =
    incidents && incidents.length > 0
      ? incidents.reduce(
          (max, i) => (i.last_seen_at > max ? i.last_seen_at : max),
          incidents[0]!.last_seen_at,
        )
      : null;

  return (
    <div className="page errors-page">
      <header className="page-header">
        <h1>Errors</h1>
        <span className="freshness-chip">
          {lastActivity !== null
            ? `Last incident activity: ${fmtDateTime(lastActivity)}`
            : "No open incidents — detector liveness on /status"}
        </span>
      </header>

      <div className="filter-bar">
        <label>
          Status
          <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="">All</option>
            {STATUSES.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Source
          <select value={sourceFilter} onChange={(e) => setSourceFilter(e.target.value)}>
            <option value="">All</option>
            {SOURCES.map((s) => (
              <option key={s} value={s}>
                {s.replace(/_/g, " ")}
              </option>
            ))}
          </select>
        </label>
        <label>
          Class
          <select value={classFilter} onChange={(e) => setClassFilter(e.target.value)}>
            <option value="">All</option>
            <option value="defect">defect</option>
            <option value="business_rejection">business rejection</option>
            <option value="transient">transient</option>
          </select>
        </label>
        <button type="button" onClick={() => void load()} disabled={loading}>
          {loading ? "Loading…" : "Refresh"}
        </button>
      </div>

      {error !== null && <div className="error-block">{error}</div>}

      {incidents !== null && incidents.length === 0 && !loading && (
        <p className="empty-state">No incidents match this filter.</p>
      )}

      {incidents !== null && incidents.length > 0 && (
        <table className="data-table errors-table">
          <thead>
            <tr>
              <th>Incident</th>
              <th>Status</th>
              <th>Class</th>
              <th>Source</th>
              <th>Count</th>
              <th>Last seen</th>
              <th>Prompt</th>
            </tr>
          </thead>
          <tbody>
            {incidents.map((i) => (
              <tr key={i.id}>
                <td>
                  <Link to={`/errors/${encodeURIComponent(i.id)}`}>{i.title}</Link>
                </td>
                <td>
                  <StatusBadge status={i.status} />
                </td>
                <td>
                  <ClassBadge cls={i.error_class} />
                </td>
                <td>{i.source.replace(/_/g, " ")}</td>
                <td>{i.occurrence_count}</td>
                <td>{fmtDateTime(i.last_seen_at)}</td>
                <td>{i.suggested_prompt !== null ? "✓" : "—"}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Detail view — `/errors/:id`.
// ---------------------------------------------------------------------------

export function ErrorIncidentDetail(): JSX.Element {
  const { id } = useParams<{ id: string }>();
  const [incident, setIncident] = useState<ErrorIncidentRow | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [loading, setLoading] = useState<boolean>(false);
  const [note, setNote] = useState<string>("");
  const [copied, setCopied] = useState<boolean>(false);
  const [busy, setBusy] = useState<boolean>(false);

  const load = useCallback(async (): Promise<void> => {
    if (id === undefined) return;
    setLoading(true);
    setError(null);
    const r = await dispatchAction<Record<string, unknown>, ListErrorIncidentsOutput>(
      "list_error_incidents",
      { id, limit: 1 },
    );
    if (r.ok) {
      setIncident(r.response.incidents[0] ?? null);
    } else {
      setError(`${r.message} (${r.code})`);
    }
    setLoading(false);
  }, [id]);

  useEffect(() => {
    void load();
  }, [load]);

  const dispose = useCallback(
    async (disposition: "resolve" | "dismiss" | "reopen" | null): Promise<void> => {
      if (id === undefined) return;
      setBusy(true);
      const input: Record<string, unknown> = { incident_id: id };
      if (disposition !== null) input["disposition"] = disposition;
      if (note.trim().length > 0) input["note"] = note.trim();
      const r = await dispatchAction<Record<string, unknown>, unknown>(
        "update_error_incident",
        input,
      );
      if (r.ok) {
        setNote("");
        await load();
      } else {
        setError(`${r.message} (${r.code})`);
      }
      setBusy(false);
    },
    [id, note, load],
  );

  const copyPrompt = useCallback(async (): Promise<void> => {
    if (incident?.suggested_prompt == null) return;
    try {
      await navigator.clipboard.writeText(incident.suggested_prompt);
      setCopied(true);
      window.setTimeout(() => setCopied(false), 2000);
    } catch {
      setCopied(false);
    }
  }, [incident]);

  if (loading && incident === null) return <div className="page">Loading…</div>;
  if (error !== null && incident === null)
    return (
      <div className="page">
        <Link to="/errors">← Errors</Link>
        <div className="error-block">{error}</div>
      </div>
    );
  if (incident === null)
    return (
      <div className="page">
        <Link to="/errors">← Errors</Link>
        <p className="empty-state">Incident not found.</p>
      </div>
    );

  const qualification =
    incident.qualification !== null
      ? JSON.stringify(incident.qualification, null, 2)
      : null;

  return (
    <div className="page errors-detail">
      <Link to="/errors">← Errors</Link>
      <header className="page-header">
        <h1>{incident.title}</h1>
        <StatusBadge status={incident.status} />
        <ClassBadge cls={incident.error_class} />
      </header>

      {error !== null && <div className="error-block">{error}</div>}

      <section className="panel">
        <dl className="kv">
          <dt>Action</dt>
          <dd>
            <code>{incident.action}</code>
          </dd>
          <dt>Outcome</dt>
          <dd>
            <code>{incident.outcome_code}</code>
          </dd>
          <dt>Source</dt>
          <dd>{incident.source.replace(/_/g, " ")}</dd>
          <dt>Occurrences</dt>
          <dd>{incident.occurrence_count}</dd>
          <dt>First seen</dt>
          <dd>{fmtDateTime(incident.first_seen_at)}</dd>
          <dt>Last seen</dt>
          <dd>{fmtDateTime(incident.last_seen_at)}</dd>
          <dt>Discriminators</dt>
          <dd>
            <code>{JSON.stringify(incident.discriminators)}</code>
          </dd>
          <dt>Signature</dt>
          <dd>
            <code>{incident.signature.slice(0, 16)}…</code>
          </dd>
        </dl>
      </section>

      {qualification !== null && (
        <section className="panel">
          <h2>AI qualification</h2>
          <pre className="code-block">{qualification}</pre>
        </section>
      )}

      <section className="panel">
        <h2>
          Fix-prompt{" "}
          {incident.prompt_template_version !== null && (
            <span className="muted">({incident.prompt_template_version})</span>
          )}
        </h2>
        {incident.suggested_prompt !== null ? (
          <>
            <button type="button" onClick={() => void copyPrompt()}>
              {copied ? "Copied ✓" : "Copy prompt"}
            </button>
            <pre className="code-block prompt-block">{incident.suggested_prompt}</pre>
          </>
        ) : (
          <p className="muted">
            No prompt yet — the laptop recon lane drafts one for a defect on its
            next tick.
          </p>
        )}
      </section>

      <section className="panel">
        <h2>Disposition</h2>
        <textarea
          placeholder="Optional note (recorded with your name + a timestamp)…"
          value={note}
          onChange={(e) => setNote(e.target.value)}
          rows={2}
        />
        <div className="button-row">
          <button type="button" onClick={() => void dispose("resolve")} disabled={busy}>
            Resolve
          </button>
          <button type="button" onClick={() => void dispose("dismiss")} disabled={busy}>
            Dismiss
          </button>
          <button type="button" onClick={() => void dispose("reopen")} disabled={busy}>
            Reopen
          </button>
          <button
            type="button"
            onClick={() => void dispose(null)}
            disabled={busy || note.trim().length === 0}
          >
            Add note
          </button>
        </div>
        {incident.notes !== null && (
          <pre className="code-block notes-block">{incident.notes}</pre>
        )}
      </section>
    </div>
  );
}
